import React, { useContext, useEffect, useRef, useState } from "react";
import { GreenMachineContext } from "../GreenMachineContext";
import {
  commitMutation,
  graphql,
  useQueryLoader,
  usePreloadedQuery,
} from "react-relay";
import relayEnvironment from "../../../../../RelayEnvironment";
import { COMMIT_BUILD_RULE_CHANGES } from "./mutations/CommitBuildRuleChanges";
import "./ConfigureRules.css";
import StackCreator from "./StackCreator";
import StackList from "./StackCreator/StackList";
import GlobalRulesList from "./GlobalRulesList";
import GCStaffStacksList from "./StackCreator/GCStaffStacksList";

export const GET_STACKS_FOR_SESSION = graphql`
  query ConfigureRulesStackListQuery($greenMachineSessionID: ID!) {
    stackRulesForGreenMachineSession(
      greenMachineSessionId: $greenMachineSessionID
    ) {
      id
      newPlayers {
        edges {
          node {
            slatePlayer {
              player {
                name
              }
            }
            greenmachinesessionstackruleplayerSet {
              edges {
                node {
                  locked
                }
              }
            }
          }
        }
      }
      stackRule {
        displayName
      }
      numLineups
      stackValue
      required
      gcStaffStack
    }
  }
`;

export default function ConfigureRules(props) {
  const {
    greenMachineSessionID,
    selectedSportID,
    selectedSlateID,
    currentStage,
    requestNavigation,
    registerStepNavGuard,
    clearStepNavGuard,
    isNavigating,
    setErrorMessage,
    isCptMode,
    STAGE_NUMBERS,
  } = useContext(GreenMachineContext);

  const [globalRuleChanges, setGlobalRuleChanges] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showStackCreator, setShowStackCreator] = useState(false);

  const [stackListQueryReference, stackListLoadQuery] = useQueryLoader(
    GET_STACKS_FOR_SESSION,
    props.initialQueryRef
  );

  if (stackListQueryReference == null && greenMachineSessionID) {
    stackListLoadQuery({ greenMachineSessionID: greenMachineSessionID });
  }

  const STAGE_NUMBER = STAGE_NUMBERS.configureRules;

  // Persist global-rule changes on the way out (Continue or a chevron jump).
  const saveRef = useRef();
  saveRef.current = () =>
    new Promise((resolve) => {
      if (!unsavedChanges) {
        resolve(true);
        return;
      }

      const changes = Object.entries(globalRuleChanges).map(([key, value]) => {
        return { buildRuleId: key, status: value };
      });

      commitMutation(relayEnvironment, {
        mutation: COMMIT_BUILD_RULE_CHANGES,
        variables: {
          input: {
            greenMachineSessionId: greenMachineSessionID,
            buildRuleChanges: changes,
          },
        },
        onCompleted: (content, errors) => {
          if (!errors) {
            setUnsavedChanges(false);
            resolve(true);
          } else {
            setErrorMessage(errors[0].errorMessage);
            resolve(false);
          }
        },
        onError: (error) => {
          console.log(error);
          setErrorMessage("Something went wrong saving your changes.");
          resolve(false);
        },
      });
    });

  useEffect(() => {
    if (currentStage !== STAGE_NUMBER) {
      return undefined;
    }
    registerStepNavGuard({ saveAndValidate: () => saveRef.current() });
    return () => clearStepNavGuard();
  }, [currentStage, STAGE_NUMBER, registerStepNavGuard, clearStepNavGuard]);

  if (currentStage < STAGE_NUMBER) {
    return null;
  }

  if (currentStage > STAGE_NUMBER) {
    return null;
  }

  function updateGlobalRuleSelections(globalRuleId, checked) {
    setUnsavedChanges(true);
    setGlobalRuleChanges({ ...globalRuleChanges, [globalRuleId]: checked });
  }

  return (
    <div className="gm-configure-rules-container">
      <div className="gm-step-actions">
        <button
          type="button"
          className="gm-btn-primary"
          disabled={isNavigating}
          onClick={() => requestNavigation(STAGE_NUMBERS.selectExposures)}
        >
          Continue
          <svg className="gm-cta-arrow" viewBox="0 0 24 24">
            <path d="M8 5l8 7-8 7V5z" />
          </svg>
        </button>
      </div>

      <div className="gm-rules-stack-lists">
        <React.Suspense fallback={<></>}>
          <GCStaffStacksList
            slateID={selectedSlateID}
            greenMachineSessionID={greenMachineSessionID}
            reloadStackList={stackListLoadQuery}
          />
        </React.Suspense>
        <div className="gm-rules-select">
          <React.Suspense fallback={<p>Loading global rules...</p>}>
            <GlobalRulesList
              globalRuleChanges={globalRuleChanges}
              updateGlobalRuleSelections={updateGlobalRuleSelections}
            />
          </React.Suspense>

          <div className="gm-rules-select-stack">
            <h3>My Stack Rules</h3>
            <React.Suspense fallback={<p>Loading stack creator...</p>}>
              <StackCreator
                show={showStackCreator}
                setShow={setShowStackCreator}
                reloadStackList={stackListLoadQuery}
              />
              <button onClick={() => setShowStackCreator(true)}>
                Create New Stack Rule
              </button>
            </React.Suspense>

            <React.Suspense fallback={<div>Loading stack list...</div>}>
              <StackList
                queryReference={stackListQueryReference}
                reloadStackList={stackListLoadQuery}
              />
            </React.Suspense>
          </div>
        </div>
      </div>
    </div>
  );
}
